import { useEffect } from 'react';
import { getServerSession } from 'next-auth';
import type { GetServerSideProps } from 'next';
import type { AxiosResponse } from 'axios';
import axios, { isAxiosError } from 'axios';
import getConfig from 'next/config';
import StatusCode from 'status-code-enum';
import {
  BreadcrumbJsonLd,
  NextSeo,
  ProductJsonLd,
  ProductJsonLdProps,
} from 'next-seo';
import Head from 'next/head';

import { AdDetailsProvider } from '../../../contexts/AdDetailsContext';
import { VerificationProvider } from '../../../contexts/VerificationContext';
import type { AdDetails as AdDetailsI } from '../../../api/types/adviewApiTypes';
import { DefaultLayout } from '../../../components/Layouts';
import { AdDetails } from '../../../components/PageWrappers/AdDetails/AdDetails';
import { adViewApi } from '../../../api/adViewApi';
import {
  getAdDetailsSEO,
  getCarAdDetailsExtraHeadTags,
  getAdDetailsProductJsonLd,
} from '../../../helpers/seo/adDetailsSeo';
import type { AdDetailsSeo } from '../../../helpers/seo/adDetailsSeo';
import { authOptions } from '../../../pages/api/auth/[...nextauth]';
import Error from '../../../pages/_error';
import { getServerCookie } from '../../../utils';
import { youtubeReviewApi } from '../../../api/youtubeReviewApi';
import type {
  IFinanceBanner,
  Notifications,
  UserCookieInfo,
} from '../../../types';
import { PAGE } from '../../../helpers/pages';
import { generateRecaptchaToken } from '../../../helpers/reCaptcha';
import { carValuationApi } from '../../../api/carValuationApi';
import { findAttributeValues } from '../../../helpers/findAttributeValues';
import {
  getMileageUnitFromString,
  getMileageValueFromString,
} from '../../../components/PageWrappers/AdDetails/helpers';
import {
  MileageIndicator,
  MileageIndicatorRequest,
} from '../../../api/types/CarValuationApiTypes';

import { isOk, isError } from '../../../domains/Result';

import { orderRepository } from '../../../repositories/Order/OrderRepository';

import { HISTORY_CHECK_REPORT } from '../../../components/Toolkit/HistoryCheckModal/HistoryCheckModal.typed';
import { formatAuthorizationHeader } from '../../../helpers/auth';
import MetricService from '../../../services/MetricService';
import { getDidomiConsents } from '../../../utils/GetDidomiConsents';
import { shouldDisplayAds } from '../../../components/PageWrappers/AdDetails/utils/shouldDisplayAds/shouldDisplayAds';
import { logger } from '@dist-marketplace-frontend/services';

import { VENDORS } from '../../../types/didomi';

const {
  publicRuntimeConfig: {
    ENVIRONMENT_NAME,
    DFP_NETWORK_ID,
    AD_SENSE_PUBLIC_KEY,
  },
} = getConfig();

interface SuccessfulServerSideProps extends AdDetailsI {
  type?: 'success';
  domain?: string;
  seoAdDetails: AdDetailsSeo;
  productJsonLdData: ProductJsonLdProps;
  financeInfo: IFinanceBanner | null;
  campaignId?: string | null;
  buttonDisplayText?: string;
  depositEnabled: boolean;
  notificationData?: Notifications;
  isPreviewAd?: boolean;
  unreadInAppMessage?: string;
  mileageIndicator: MileageIndicator | null;
  displayDfpUnits: boolean;
}

interface ErrorServerSideProps {
  type: 'error';
  errorCode: number;
  domain?: string;
}

type AdDetailsProps = SuccessfulServerSideProps | ErrorServerSideProps;
function AdDetailsPage(props: AdDetailsProps) {
  useEffect(() => {
    generateRecaptchaToken('addetails_page');
  }, []);

  if (props.type === 'error') {
    return (
      <Error
        statusCode={props.errorCode}
        disableGtmLog={true}
        domain={props.domain}
      />
    );
  }

  const {
    domain = '',
    seoAdDetails,
    productJsonLdData,
    campaignId,
    userSaved,
    breadcrumbs,
    notificationData,
    unreadInAppMessage,
    displayDfpUnits,
  } = props;

  const breadcrumbJsonLdItems = breadcrumbs.map((breadcrumb, index) => ({
    position: index + 1,
    name: breadcrumb.displayName as string,
    item: `${domain}/${breadcrumb.name!}`,
  }));

  return (
    <AdDetailsProvider userSaved={userSaved} displayDfpUnits={displayDfpUnits}>
      <VerificationProvider>
        <NextSeo {...seoAdDetails} />
        <BreadcrumbJsonLd itemListElements={breadcrumbJsonLdItems} />
        <Head>
          {/* TODO: Add below to next-seo where it makes sense */}
          <meta property="product:price:amount" content={`${props.price}.00`} />
          <meta property="product:price:currency" content={props.currency} />
          <meta property="og:availability" content="in stock" />
          <meta property="og:price:amount" content={`${props.price}.00`} />
          <meta property="og:price:currency" content={props.currency} />
          <meta property="product:availability" content="in stock" />
          <meta property="product:condition" content="used" />
          <meta property="product:retailer_item_id" content={`${props.id}`} />
          {getCarAdDetailsExtraHeadTags(props)}
        </Head>

        <ProductJsonLd {...productJsonLdData} />
        <DefaultLayout
          domain={domain}
          notificationData={notificationData}
          unreadInAppMessage={unreadInAppMessage}
        >
          <AdDetails
            baseUrl={domain}
            dfpNetworkId={DFP_NETWORK_ID}
            environmentName={ENVIRONMENT_NAME}
            campaignId={campaignId}
            userSaved={userSaved}
            adSensePublicKey={AD_SENSE_PUBLIC_KEY}
            {...props}
          />
        </DefaultLayout>
      </VerificationProvider>
    </AdDetailsProvider>
  );
}

export const getServerSideProps: GetServerSideProps<
  SuccessfulServerSideProps | ErrorServerSideProps,
  { adId: string; section: string; title: string }
> = async (ctx) => {
  try {
    const { params, query } = ctx;
    const section = params?.section;
    const adId = params?.adId;
    // Determine if ad ID is a valid value i.e. number
    const adIdAsNumber = adId && parseInt(adId);
    const { headers } = ctx.req;
    const { cookie, 'user-agent': userAgent } = headers;

    const historyCheckRequired = query.api_request === 'history-check';
    const invokeHistoryCheckApi = historyCheckRequired && adId;

    if (invokeHistoryCheckApi && adIdAsNumber) {
      const session = await getServerSession(ctx.req, ctx.res, authOptions);

      const historyReport = await orderRepository.historyReport(
        {
          type: HISTORY_CHECK_REPORT.COMPLETE_REPORT,
          param: adIdAsNumber.toString(),
        },
        formatAuthorizationHeader(session?.accessToken),
      );

      if (isOk(historyReport)) {
        const orderID = historyReport.ok.id;
        return {
          redirect: {
            permanent: false,
            destination: `/order/${orderID}`,
          },
        };
      }

      if (isError(historyReport)) {
        const error = historyReport.error;
        logger.error({
          error,
          message: 'Error fetching history report',
          route: PAGE.AD_DETAILS,
        });
      }
    }

    /**
     * query param can be of type string[] due to catch all routes, but we know
     * campaign is a string parameter at end of url, so retyped here.
     */
    let { campaign: campaignId }: { campaign?: string | null } = query;

    /**
     * Can't have undefined props returned from getServerSideProps currently:
     * https://github.com/vercel/next.js/discussions/11209
     */
    if (campaignId === undefined) {
      campaignId = null;
    }

    // Supported routes
    const FOR_SALE_ROUTE = Boolean(
      typeof section === 'string' && /-for-sale$/.test(section),
    );
    const WANTED_ROUTE = Boolean(
      typeof section === 'string' && /-sold$/.test(section),
    );
    const SOLD_ROUTE = Boolean(
      typeof section === 'string' && /-wanted$/.test(section),
    );

    /**
     * To allow for pingdom and/or test such as pingdom we the following route:
     * -> www.donedeal.ie/latest-for-sale
     *
     * If this route is hit, we will hit a special endpoint that will return
     * the data for the latest ad that has been published.
     *
     * From here we render the page as normal.
     */
    const isAutomatedTestOfAdDetails = Boolean(section === 'latest-for-sale');

    /**
     * This is used for short urls and is mainly to support old urls
     * Example routes that will be redirected to this one:
     * https://www.donedeal.ie/adview/view/${id}
     * https://www.donedeal.ie/view/${id}
     * Which redirect to this route: https://www.donedeal.ie/view/listing/${id}
     */
    const GENERIC_AD_ROUTE = Boolean(
      typeof section === 'string' && section === 'view',
    );

    // If it is not a matching route, return a 404
    if (!FOR_SALE_ROUTE && !WANTED_ROUTE && !SOLD_ROUTE && !GENERIC_AD_ROUTE) {
      return { notFound: true };
    }

    /**
     * If no adId is found we set the statusCode to 404 and respond.
     * We also do not catch any errors here as we want to allow Next to handle.
     */
    if (!params?.adId && !isAutomatedTestOfAdDetails) {
      return { notFound: true };
    }

    let adData: AdDetailsI;
    let notificationData;
    let unreadInAppMessage;

    if (isAutomatedTestOfAdDetails) {
      const {
        data,
        headers: {
          unreadhistorychecks,
          unreadmessages,
          totalunread,
          unreadiam,
        },
      }: AxiosResponse<AdDetailsI> = await adViewApi.getLatestAdId();
      adData = { ...data };

      notificationData = {
        totalUnread: totalunread ?? null,
        unreadHistoryChecks: unreadhistorychecks ?? null,
        unreadMessages: unreadmessages ?? null,
      };

      unreadInAppMessage = unreadiam ?? null;
    } else if (adIdAsNumber) {
      const ddRecommendedUser = getServerCookie('ddRecommendedUser', ctx, true);

      const getAdRequestHeaders = {
        ...(ddRecommendedUser && {
          dd_recommendation_user: ddRecommendedUser.toString(),
        }),
        ...(cookie && { cookie }),
        ...(userAgent && { 'user-agent': userAgent }),
        'x-forwarded-for': ctx.req.headers['cf-connecting-ip'],
      };

      const {
        data,
        headers: {
          unreadhistorychecks,
          unreadmessages,
          totalunread,
          unreadiam,
        },
      }: AxiosResponse<AdDetailsI> = await adViewApi.getAd(
        adIdAsNumber,
        query,
        getAdRequestHeaders,
      );
      adData = { ...data };

      unreadInAppMessage = unreadiam ?? null;

      notificationData = {
        totalUnread: totalunread ?? null,
        unreadHistoryChecks: unreadhistorychecks ?? null,
        unreadMessages: unreadmessages ?? null,
      };
    } else {
      return { notFound: true };
    }

    /**
     * Use car valuation API to get the mileage indicator configuration
     * We will only make the mileage indicator call for 'cars' section for now
     */
    let mileageIndicator: MileageIndicator | null = null;
    try {
      const isCarsSection = adData.section.name === 'cars';
      if (adData.displayAttributes && isCarsSection) {
        const {
          make,
          model,
          year,
          fuelType,
          mileage,
          bodyType,
          engine: engineSize,
        } = findAttributeValues(adData.displayAttributes, [
          'make',
          'model',
          'year',
          'fuelType',
          'mileage',
          'bodyType',
          'engine',
        ]);

        if (make && model && year && fuelType && bodyType && mileage) {
          const mileageValue = getMileageValueFromString(mileage);
          const mileageUnit = getMileageUnitFromString(mileage);
          const yearValue = parseInt(year);
          let payload: MileageIndicatorRequest = {
            fuelType,
            bodyType,
            year: yearValue,
            sectionName: 'cars',
            mileage: mileageValue,
            mileageType: mileageUnit,
            engineSize,
          };

          const response = await carValuationApi.postCarMileageIndicator(
            payload,
          );

          const { state, medianMileage, mileageDifference } = response.data;
          if (state && medianMileage && mileageDifference) {
            mileageIndicator = {
              state,
              medianMileage,
              mileageDifference,
              make,
              model,
              year: yearValue,
              bodyType,
              fuelType,
            };
          }
        }
      }
    } catch (error) {
      logger.error({
        error,
        message: 'Error retrieving mileage data',
        route: PAGE.AD_DETAILS,
      });
    }

    // Only look for YouTube review if there is other media content and there is
    // a make, model and year
    if (adData.mediaCount && adData.displayAttributes) {
      const make = adData.displayAttributes.find(
        (item) => item.name === 'make',
      );
      const model = adData.displayAttributes.find(
        (item) => item.name === 'model',
      );
      const year = adData.displayAttributes.find(
        (item) => item.name === 'year',
      );
      const fuelType = adData.displayAttributes.find(
        (item) => item.name === 'fuelType',
      );
      const bodyType = adData.displayAttributes.find(
        (item) => item.name === 'bodyType',
      );
      if (make && model && year) {
        try {
          const { data } = await youtubeReviewApi.getAdYoutubeReviewId(
            make.value as string,
            model.value as string,
            year.value as string,
            fuelType?.value as string,
            bodyType?.value as string,
          );
          if (data?.id) {
            adData.youTubeReviewVideoId = data.id;
            if (adData.mediaCount) {
              adData.mediaCount += 1;
            }
          }
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to get video review data',
            route: PAGE.VIDEO_REVIEWS,
          });
        }
      }
    }

    /**
     * Check if user is an Admin
     */
    const IS_ADMIN = getServerCookie('UserIsAdminHint', ctx, false);
    if (typeof IS_ADMIN !== 'undefined') {
      adData.isAdmin = IS_ADMIN;
    }

    /**
     * If not published we will trigger 308/301 to its section
     */
    if (adData.state !== 'Published' && !adData.displayExpiredForSeo) {
      ctx.res.setHeader(
        'Cache-Control',
        'private, no-cache, no-store, must-revalidate',
      );
      ctx.res.setHeader('Expires', 'Thu, 01 Jan 1970 00:00:00 GMT');
      let path = '';
      const { breadcrumbs, section } = adData;
      // monolith uses returns breadcrumbs
      if (breadcrumbs) {
        // we get the deepest section and use this for redirect
        path = breadcrumbs[breadcrumbs.length - 1].name || 'all';
      } else if (section) {
        // new be service returns section instead of breadcrumbs
        path = section.name || 'all';
      }

      return {
        redirect: {
          permanent: true,
          destination: `/${path}?notFound=1`,
        },
      };
    }

    /**
     * We should only ever have a single URL that an ad can be viewed on
     * We will eventually remove all links to these kinda pages but for
     * now we must redirect them
     */
    if (GENERIC_AD_ROUTE || isAutomatedTestOfAdDetails) {
      return {
        redirect: {
          permanent: true,
          destination: adData.friendlyUrl as string,
        },
      };
    }

    // If ad is published we do some set up
    const { cardPaymentsV2 } = adData;

    const filteredByDeposit = cardPaymentsV2?.filter(
      (entry) => entry.cardPaymentType === 'DEPOSIT',
    );

    const depositEnabled = Boolean(
      filteredByDeposit && filteredByDeposit?.length > 0,
    );

    const buttonDisplayText =
      filteredByDeposit && filteredByDeposit.length > 0
        ? filteredByDeposit[0].displayText
        : 'Buy Now';

    const buyNowSuccess = query.buyNow === 'success';

    const seoAdDetails = getAdDetailsSEO(adData);
    const breadcrumbs = seoAdDetails.breadcrumbs;
    const productJsonLdData = getAdDetailsProductJsonLd(adData);

    let financeInfo: IFinanceBanner | null = null;
    if (adData.finance?.bannerUrl) {
      /**
       * Backend now make the call and add this on their end. We still
       * fallback to calling out to the service to account for cached ads
       */
      if (adData.finance.banner?.title?.length) {
        financeInfo = adData.finance.banner;
      }
    }

    const didomiToken: string | undefined = getServerCookie(
      'didomi_token',
      ctx,
      true,
    );

    const consents = didomiToken
      ? getDidomiConsents({
          token: didomiToken,
          onError: () =>
            logger.error({
              route: PAGE.AD_DETAILS,
              message: 'Failed to decode Didomi token',
            }),
        })
      : null;

    const cookieUser = getServerCookie('DDUS', ctx, true) as
      | UserCookieInfo
      | undefined;

    const displayDfpUnits = await shouldDisplayAds({
      sectionName: adData.section.name,
      isDealer: Boolean(adData.dealer),
      allowConvert:
        consents?.enabledVendors?.includes(VENDORS.CONVERT) ?? false,
      userId: cookieUser?.id ? String(cookieUser.id) : undefined,
    });

    return {
      props: {
        ...adData,
        dataTargetingProps: {
          dfp: adData.dataTargetingProps?.dfp || {},
          gtm: adData.dataTargetingProps?.gtm || {},
        },
        seoAdDetails,
        productJsonLdData,
        buyNowSuccess,
        financeInfo,
        campaignId,
        breadcrumbs,
        buttonDisplayText,
        depositEnabled,
        notificationData,
        unreadInAppMessage,
        mileageIndicator,
        displayDfpUnits,
      },
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const metricService = MetricService.get();
      metricService.incrementRequestErrorCount('AD_DETAILS', {
        statusCode: error?.response?.status,
      });
    }

    if (isAxiosError(error) && error.code === 'ECONNABORTED') {
      ctx.res.statusCode = StatusCode.ServerErrorGatewayTimeout;
      logger.error({
        route: PAGE.AD_DETAILS,
        error,
        message: 'Server timeout',
      });
      return {
        props: {
          type: 'error',
          errorCode: StatusCode.ServerErrorGatewayTimeout,
        },
      };
    }

    /**
     * For older ads that do not exist in DB anymore a 404 will be triggered.
     */
    if (
      isAxiosError(error) &&
      error?.response?.status === StatusCode.ClientErrorNotFound
    ) {
      logger.error({
        route: PAGE.AD_DETAILS,
        error,
        message: 'Ad not found. Ad may no longer exist in DB',
      });
      return {
        notFound: true,
      };
    }

    if (
      isAxiosError(error) &&
      error?.response?.status === StatusCode.ClientErrorBadRequest
    ) {
      logger.error({
        route: PAGE.AD_DETAILS,
        error,
        message: 'Bad client request',
      });
      ctx.res.statusCode = StatusCode.ClientErrorBadRequest;
      return {
        props: {
          type: 'error',
          errorCode: StatusCode.ClientErrorBadRequest,
        },
      };
    }

    // Signal that there is an error
    ctx.res.statusCode = StatusCode.ServerErrorInternal;
    logger.error({
      route: PAGE.AD_DETAILS,
      error,
      message: 'Unknown server error',
    });
    return {
      props: {
        type: 'error',
        errorCode: StatusCode.ServerErrorInternal,
      },
    };
  }
};

export default AdDetailsPage;
